import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import { isEqual } from "lodash";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";

import { getArchiveConditionByWorkspaceID, setConditionUnarchive } from "../../Api/Services/ConditionsServices";
import { getArchivePetNamesByWorkspaceID } from "../../Api/Services/PetServices";
import { ReactComponent as ViewIcon } from "../../Assets/Images/viewIcon.svg";
import useAuth from '../../ContextProvider/AuthProvider'
import { filterAscDescData, hexToRgba } from "../../Utils";
import DetailsModal from "../Common/DetailsModal";
import PawItemList from "../Common/PawItemList.js";
import { useTheme } from "@mui/material/styles";

import "./ArchiveGlobalItemList.css";
import { Button } from "@mui/material";

export default function ArchiveCondition() {
  const { workspaceId: w_id } = useAuth();
  const [selectedPetName, setSelectedPetName] = useState([]);
  const [open, setOpen] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [archiveId, setArchiveId] = useState(null);
  const [filter, setFilter] = useState({});
  const [pageCount, setPageCount] = useState(0);
  const [selConditionData, setSelConditionData] = useState([]);
  const fetchIdRef = useRef(0);
  const theme = useTheme();

  const navigate = useNavigate();

  const handleArchiveClick = (medication_id) => {
    setShowSuccessMessage(true);
    setOpen(true);
    setArchiveId(medication_id); 
  };
  const handleArchive = () => {
    setIsUpdating(true);

    setConditionUnarchive(archiveId)
      .then((response) => {
        setIsUpdating(false);
        if (response.data.success === true) {
          window.location.reload();
        }
      })
      .catch((err) => {
        console.error(err);
        setOpen(true);
        setIsUpdating(false);
      });
  };

  const { data: conditionsData = [], isLoading } = useSWR(
    w_id ? `archiveConditionsData${w_id}` : null,
    async () => {
      const { data } = await getArchiveConditionByWorkspaceID(w_id);
      const Dates = data?.data.map((item) => {
        const date = (
          <>
            {new Date(item?.date).getFullYear()}-
            {(new Date(item?.date).getMonth() + 1).toString().padStart(2, "0")}-
            {new Date(item?.date).getDate().toString().padStart(2, "0")}
          </>
        );
        return { ...item, id: item?.condition_id, date: date };
      });
      return Dates || [];
    },
    {
      revalidateOnFocus: false,
    }
  );

  const fetchData = useCallback((oFilter) => {
    const fetchId = ++fetchIdRef.current;
    if (fetchId === fetchIdRef.current && !isEqual(filter, oFilter)) {
      setFilter(oFilter);
    }
  }, []);

  useEffect(() => {
    const { limit, from, sort, } = filter;
    if (filter && filter.from > -1) {
      if (conditionsData?.length && limit) {
        setPageCount(Math.ceil((conditionsData?.length) / limit));
        const allData = [...conditionsData];
        const filteredResult = filterAscDescData(allData, sort, from, limit);
        setSelConditionData(filteredResult);
      }
    }
  }, [filter, conditionsData]);

  const { data: petNames = [] } = useSWR(
    w_id ? `petNames${w_id}` : null,
    async () => {
      const { data } = await getArchivePetNamesByWorkspaceID(w_id);
      return data?.data || [];
    },
    {
      revalidateOnFocus: false,
    }
  );

  let filteredConditionData;

  if (selectedPetName === "") {
    filteredConditionData = selConditionData;
  } else if (!selConditionData) {
    filteredConditionData = [];
  } else {
    filteredConditionData = selConditionData.filter((item) => {
      const petName = item.name || "";
      const query =
        selectedPetName && selectedPetName.length > 0
          ? selectedPetName[0].toLowerCase()
          : "";
      return petName.toLowerCase().includes(query);
    });
  }

  const petNamesSet = new Set();
  const filteredPetNames = petNames.filter((pet) => {
    const lowercaseName = pet.name.toLowerCase();
    if (!petNamesSet.has(lowercaseName)) {
      petNamesSet.add(lowercaseName);
      return true;
    }
    return false;
  });

  const columns = useMemo(() => {
    return [
      {
        Header: "",
        accessor: "image",
        width: "8vw",
        disableSortBy: true,
        Cell: ({ row }) => {
          return row?.original?.image ? (
            <img
              alt="petImage"
              src={row?.original?.image}
              style={{
                width: "50px",
                height: "50px",
                borderRadius: "10px",
                objectFit: "cover",
                margin: "0 auto",
                verticalAlign: "middle",
              }}
            />
          ) : (
            <AccountCircleIcon
              sx={{
                width: "50px",
                height: "50px",
              }}
            />
          );
        },
      },
      {
        Header: "Pet Name",
        accessor: "name",
        width: "12.5vw",
      },
      {
        Header: `Condition`,
        accessor: "conditions",
        width: "12.5vw",
      },
      {
        Header: "Notes",
        accessor: "condition_note",
        width: "12.5vw",
      },
      {
        Header: "",
        accessor: "condition_id",
        width: "8vw",
        isArchive: true,
        disableSortBy: true,
        disabled: isUpdating,
        textAlign: "center",
        Cell: ({ row }) => {
          return (
            <UnarchiveIcon
              style={{
                color: "#000000",
              }}
              onClick={() =>
                handleArchiveClick(row?.original?.condition_id)
              }
            />
          );
        },
      },
      {
        Header: "Action",
        accessor: "action",
        width: "8vw",
        disableSortBy: true,
        textAlign: "center",
        Cell: ({ row }) => {
          return (
            <Button
            sx={{
              minWidth: "40px",
              background: `${hexToRgba(theme.palette.primary.main, 0.20)} !important`,
              borderRadius: "10px",
              marginRight: "5px",
              width: "24px",
            }}
            onClick={() => navigate(props.viewDataLink(row.original))}
          >
            <ViewIcon
                style={{
                  fill: `${theme.palette.primary.main}`, 
                  cursor: "pointer",
                }}
                alt="view"
              />
          </Button>
          );
        },
      },
    ];
  }, []);

  const defaultOptions = {
    pageSize: 5,
  };

  const props = {
    label: "Archive Conditions",
    setSelectedUserName: setSelectedPetName,
    items: filteredPetNames,
    isLoading,
    dataAddLink: "/Archive",
    columns,
    columnsData: filteredConditionData,
    viewDataLink: (data) => `/archive/conditions-info/${data.condition_id}`,
    placeholder: "pet name",
    details: "There are no condition details for this user.",
    isArchive: true,
    AddPetArchive: true,
    fetchData,
    pageCount,
    defaultOptions,
  };

  const ButtonItem = [
    {
      name: "yes",
      onClick: () => {
        handleArchive();
        setShowSuccessMessage(false);
      },
    },
    {
      name: "no",
      onClick: () => {
        setOpen(false);
        setShowSuccessMessage(false);
      },
    },
  ];

  return (
    <>
      <PawItemList {...props} />
      {showSuccessMessage && (
        <>
          <DetailsModal
            open={open}
            severity="warning"
            buttonList={ButtonItem}
            title="Do you want to unarchive this condition?"
            handleClose={()=> setOpen(false)}
          />
        </>
      )}
    </>
  );
}
